<script setup lang="ts">
  import { computed, getCurrentInstance } from 'vue';
  import PosUiButton from '@/components/pos-v2/ui/PosUiButton.vue';
  import PosUiIcon from '@/components/pos-v2/ui/PosUiIcon.vue';
  import PosUiPopover from '@/components/pos-v2/ui/PosUiPopover.vue';
  import {
    relativeMinutesLabel,
    reservationWaitlistTiming,
  } from '@/composition/reservations/use-reservations-waitlist';
  import type { TReservationWaitlistAction } from '@/composition/reservations/use-reservations-waitlist';
  import { formatReservationPhone } from '@/domain/reservations/reservation-phone';
  import {
    defaultBoardDate,
    formatTime,
  } from '@/domain/reservations/reservation-time';
  import type { IReservationWaitlist } from '@/interfaces/IReservationWaitlist';

  const props = defineProps<{
    entry: IReservationWaitlist;
    now: Date;
    timeZone: string;
    businessDayStartHour?: number;
    country: string;
    busy?: TReservationWaitlistAction;
    error?: TReservationWaitlistAction;
  }>();
  const emit = defineEmits<{
    (event: 'notify'): void;
    (event: 'seat'): void;
    (event: 'remove'): void;
    (event: 'create'): void;
  }>();
  const proxy = getCurrentInstance()!.proxy;
  const timing = computed(() =>
    reservationWaitlistTiming(props.entry, props.now)
  );
  const phone = computed(() =>
    formatReservationPhone(props.entry.guestPhone, props.country)
  );
  const expired = computed(
    () =>
      props.entry.status === 'expired' ||
      (props.entry.kind === 'online' &&
        ((props.entry.desiredStartsAt != null &&
          Date.parse(props.entry.desiredStartsAt) < props.now.getTime()) ||
          (props.entry.desiredStartsAt == null &&
            props.entry.desiredDate != null &&
            props.entry.desiredDate <
              defaultBoardDate(
                props.now,
                props.timeZone,
                props.businessDayStartHour
              )) ||
          (props.entry.status === 'notified' &&
            timing.value.notifiedMinutes != null &&
            timing.value.notifiedMinutes >= 1440)))
  );
  const notifiedText = computed(() => {
    const minutes = timing.value.notifiedMinutes ?? 0;
    if (minutes < 1)
      return String(proxy.$t('reservations.waitlist.notifiedNow'));
    const ago = relativeMinutesLabel(minutes, proxy.$i18n.locale);
    return String(proxy.$t('reservations.waitlist.notified', { ago }));
  });
  // An online request is not a guest standing at the door: say when it came in
  // ("hace 1 minuto" at the earliest, never "este minuto").
  const requestedText = computed(() =>
    String(
      proxy.$t('reservations.waitlist.requested', {
        ago: relativeMinutesLabel(
          Math.max(1, timing.value.waitingMinutes),
          proxy.$i18n.locale
        ),
      })
    )
  );
  const desired = computed(() => {
    const value =
      props.entry.desiredStartsAt ??
      (props.entry.desiredDate
        ? `${props.entry.desiredDate}T12:00:00.000Z`
        : null);
    if (!value || !Number.isFinite(Date.parse(value))) return null;
    return {
      date: new Intl.DateTimeFormat(proxy.$i18n.locale, {
        // A date-only preference stays on its service date in every zone.
        timeZone: props.entry.desiredStartsAt ? props.timeZone : 'UTC',
        weekday: 'short',
        day: 'numeric',
        month: 'short',
      }).format(new Date(value)),
      time: props.entry.desiredStartsAt
        ? formatTime(value, props.timeZone, proxy.$i18n.locale)
        : String(proxy.$t('reservations.waitlist.anyTime')),
    };
  });
</script>

<template>
  <article
    class="waitlist-row"
    :class="{
      'is-warning': entry.kind === 'walk_in' && timing.tone === 'warning',
      'is-danger': entry.kind === 'walk_in' && timing.tone === 'danger',
    }"
    :aria-busy="!!busy"
  >
    <div class="row-main">
      <div class="guest-heading">
        <h3>{{ entry.guestName }}</h3>
        <span class="party"
          ><PosUiIcon name="account-multiple-outline" />{{
            $tc('reservations.common.people', entry.partySize, {
              count: entry.partySize,
            })
          }}</span
        >
      </div>
      <a
        v-if="entry.guestPhone"
        class="phone"
        :href="`tel:${entry.guestPhone}`"
        >{{ phone }}</a
      >
      <p v-if="entry.kind === 'walk_in'" class="wait-timer">
        <PosUiIcon name="clock-outline" /><span
          >{{
            $t('reservations.waitlist.waiting', {
              minutes: timing.waitingMinutes,
            })
          }}<template v-if="entry.quotedWaitMin != null">
            ·
            {{
              $t('reservations.waitlist.quoted', {
                minutes: entry.quotedWaitMin,
              })
            }}</template
          ></span
        ><strong v-if="timing.overdueMinutes">{{
          $t('reservations.waitlist.over', { minutes: timing.overdueMinutes })
        }}</strong>
      </p>
      <p v-else-if="desired" class="desired">
        {{ $t('reservations.waitlist.wantsAt', desired) }}
      </p>
      <p v-if="expired" class="expired">
        {{ $t('reservations.waitlist.expired') }}
      </p>
      <p v-else-if="entry.notifiedAt" class="notified">
        {{ notifiedText }}
      </p>
      <p v-else-if="entry.kind === 'online'" class="desired">
        {{ requestedText }}
      </p>
      <p v-if="entry.notes" class="notes">{{ entry.notes }}</p>
      <p v-if="error" class="row-error" role="alert">
        {{
          $t(
            error === 'notify'
              ? 'reservations.waitlist.notifyError'
              : error === 'seat'
              ? 'reservations.walkIn.error'
              : 'reservations.detail.saveError'
          )
        }}
      </p>
    </div>
    <div class="row-actions">
      <PosUiButton
        v-if="entry.kind === 'walk_in'"
        color="neutral"
        variant="outline"
        icon="bell-outline"
        :disabled="!!busy || !entry.guestPhone"
        @click="emit('notify')"
        >{{
          $t(
            entry.status === 'notified'
              ? 'reservations.waitlist.notifyAgain'
              : 'reservations.waitlist.notify'
          )
        }}</PosUiButton
      >
      <PosUiButton
        v-if="entry.kind === 'walk_in'"
        color="success"
        variant="soft"
        icon="seat-outline"
        :disabled="!!busy"
        @click="emit('seat')"
        >{{ $t('reservations.waitlist.seat') }}</PosUiButton
      >
      <PosUiButton
        v-else
        color="neutral"
        variant="outline"
        icon="calendar-plus"
        :disabled="!!busy"
        @click="emit('create')"
        >{{ $t('reservations.waitlist.newBooking') }}</PosUiButton
      >
      <PosUiPopover placement="bottom" align="end" :disabled="!!busy"
        ><template #trigger="{ toggle }"
          ><PosUiButton
            color="neutral"
            variant="ghost"
            icon="dots-horizontal"
            :aria-label="$t('posV2.moreActions')"
            :disabled="!!busy"
            @click="toggle" /></template
        ><template #default="{ close }"
          ><PosUiButton
            color="error"
            variant="ghost"
            icon="close"
            block
            @click="
              close();
              emit('remove');
            "
            >{{ $t('reservations.waitlist.remove') }}</PosUiButton
          ></template
        ></PosUiPopover
      >
    </div>
  </article>
</template>

<style scoped lang="scss">
  .waitlist-row {
    @apply tw-flex tw-flex-col tw-gap-4 tw-rounded-xl tw-border tw-border-solid tw-border-neutral-200 tw-bg-white tw-p-4;
    @media (min-width: 1024px) {
      @apply tw-flex-row tw-items-center tw-justify-between;
    }
    &.is-warning {
      @apply tw-border-amber-300;
      .wait-timer {
        @apply tw-text-amber-700;
      }
    }
    &.is-danger {
      @apply tw-border-red-300;
      .wait-timer {
        @apply tw-text-red-700;
      }
    }
    .row-main {
      @apply tw-flex tw-min-w-0 tw-flex-col tw-gap-1.5;
    }
    .guest-heading {
      @apply tw-flex tw-flex-wrap tw-items-center tw-gap-x-3 tw-gap-y-1;
      h3 {
        @apply tw-m-0 tw-break-words tw-text-sm tw-font-semibold tw-text-neutral-900;
      }
    }
    .party {
      @apply tw-flex tw-items-center tw-gap-1.5 tw-text-xs tw-tabular-nums tw-text-neutral-600;
    }
    .phone {
      @apply tw-self-start tw-text-sm tw-text-neutral-500 hover:tw-text-neutral-900;
    }
    .wait-timer {
      @apply tw-m-0 tw-flex tw-flex-wrap tw-items-center tw-gap-1.5 tw-text-xs tw-tabular-nums tw-text-neutral-600;
      strong {
        @apply tw-font-semibold;
      }
    }
    .desired,
    .notified,
    .expired,
    .notes {
      @apply tw-m-0 tw-text-xs tw-text-neutral-500;
    }
    .notified {
      @apply tw-text-sky-700;
    }
    .expired {
      @apply tw-text-amber-700;
    }
    .notes {
      @apply tw-whitespace-pre-line tw-break-words;
    }
    .row-actions {
      @apply tw-flex tw-shrink-0 tw-flex-wrap tw-items-center tw-gap-2;
      :deep(.pos-ui-button) {
        @apply tw-min-w-11;
      }
      @media (max-width: 479px) {
        @apply tw-gap-1.5;
        :deep(.pos-ui-button) {
          @apply tw-px-2;
        }
      }
    }
    .row-error {
      @apply tw-m-0 tw-text-xs tw-text-red-600;
    }
  }
</style>
